/**
 * BookingV2Manager.js
 * ----------------------------------------------------------------------------
 * ADD-ON component for the Admin Dashboard. Self-contained: search vendors and
 * toggle the V2 booking engine on/off for any one of them.
 *
 * Wiring into AdminDashboard.js is 3 small additions:
 *   1. import BookingV2Manager from './BookingV2Manager';
 *   2. one <button> in the <nav> for the 'bookingV2' tab
 *   3. one line in <main>:  {activeTab === 'bookingV2' && <BookingV2Manager />}
 *
 * No existing admin code is modified. Uses the shared axios instance, so the
 * admin JWT is attached automatically (same as every other admin call).
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosConfig';

function BookingV2Manager() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/booking-v2/vendors', {
        params: q ? { search: q } : {},
      });
      setVendors(res.data);
    } catch (err) {
      console.error('Error loading vendors:', err);
      setMessage({ type: 'error', text: 'Failed to load vendors' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + lightly debounced search.
  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const toggle = async (vendor) => {
    const next = !vendor.booking_v2_enabled;
    setSavingId(vendor.id);
    setMessage(null);
    try {
      await axios.put(`/api/admin/booking-v2/vendors/${vendor.id}`, { enabled: next });
      setVendors((prev) =>
        prev.map((v) => (v.id === vendor.id ? { ...v, booking_v2_enabled: next } : v))
      );
      setMessage({
        type: 'success',
        text: `V2 booking ${next ? 'enabled' : 'disabled'} for ${vendor.business_name}`,
      });
    } catch (err) {
      console.error('Toggle error:', err);
      setMessage({ type: 'error', text: `Could not update ${vendor.business_name}` });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bv2-admin">
      <style>{`
        .bv2-admin { padding: 4px 2px 24px; }
        .bv2-admin h3 { margin: 0 0 4px; }
        .bv2-admin .bv2-sub { color: #5c6b73; margin: 0 0 18px; font-size: 14px; }
        .bv2-admin .bv2-search {
          width: 100%; max-width: 360px; padding: 10px 12px; margin-bottom: 16px;
          border: 1px solid #d9e0e3; border-radius: 8px; font-size: 14px;
        }
        .bv2-admin .bv2-msg {
          padding: 10px 14px; border-radius: 8px; margin-bottom: 16px; font-size: 14px;
        }
        .bv2-admin .bv2-msg.success { background: #e3f6ef; color: #1b6f57; }
        .bv2-admin .bv2-msg.error { background: #fdecec; color: #b23b3b; }
        .bv2-admin table { width: 100%; border-collapse: collapse; }
        .bv2-admin th, .bv2-admin td {
          text-align: left; padding: 12px 10px; border-bottom: 1px solid #eef1f2; font-size: 14px;
        }
        .bv2-admin th { color: #5c6b73; font-weight: 600; }
        .bv2-admin .bv2-pill {
          display: inline-block; padding: 2px 10px; border-radius: 999px;
          font-size: 12px; font-weight: 600;
        }
        .bv2-admin .bv2-pill.on  { background: #e3f6ef; color: #1b6f57; }
        .bv2-admin .bv2-pill.off { background: #eef1f2; color: #5c6b73; }
        .bv2-admin .bv2-btn {
          padding: 7px 14px; border-radius: 8px; border: 1px solid transparent;
          font-size: 13px; font-weight: 600; cursor: pointer;
        }
        .bv2-admin .bv2-btn.enable { background: #2fb89a; color: #fff; }
        .bv2-admin .bv2-btn.disable { background: #fff; color: #b23b3b; border-color: #f0c9c9; }
        .bv2-admin .bv2-btn:disabled { opacity: .55; cursor: default; }
      `}</style>

      <h3>V2 Booking Engine</h3>
      <p className="bv2-sub">
        Switch the new booking engine on for a vendor. Enabling also seeds default
        Mon–Fri 9–5 business hours so the calendar works out of the box; they can
        refine everything from their dashboard.
      </p>

      <input
        className="bv2-search"
        type="text"
        placeholder="Search by business name, link, or email…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {message && <div className={`bv2-msg ${message.type}`}>{message.text}</div>}

      {loading ? (
        <p>Loading vendors…</p>
      ) : vendors.length === 0 ? (
        <p>No vendors match that search.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Business</th>
              <th>Link</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => (
              <tr key={v.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{v.business_name}</div>
                  <div style={{ color: '#5c6b73', fontSize: 12 }}>{v.email}</div>
                </td>
                <td style={{ color: '#5c6b73' }}>/{v.sub_url}</td>
                <td>
                  <span className={`bv2-pill ${v.booking_v2_enabled ? 'on' : 'off'}`}>
                    {v.booking_v2_enabled ? 'Enabled' : 'Off'}
                  </span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    className={`bv2-btn ${v.booking_v2_enabled ? 'disable' : 'enable'}`}
                    disabled={savingId === v.id}
                    onClick={() => toggle(v)}
                  >
                    {savingId === v.id
                      ? 'Saving…'
                      : v.booking_v2_enabled
                      ? 'Disable'
                      : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default BookingV2Manager;